import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import type { FilterConfig } from "../../datagrid-configs/types";
import client from "libs/client";

/**
 * Filter option for select components.
 */
export interface FilterOption {
	label: string;
	value: string | number;
}

/**
 * Dynamic filter options keyed by filter ID.
 */
export type DynamicFilterOptions = Record<string, FilterOption[]>;

/**
 * Hook to fetch dynamic filter options for Clubs table via React Query.
 * Uses useQueries so each filter option request is cached and key-stable (avoids firing more than twice).
 *
 * @param filters - Array of filter configurations
 * @returns Object containing dynamic options and loading state
 */
export function useClubFilterOptions(filters: FilterConfig[]) {
	const filtersWithEndpoints = useMemo(
		() => filters.filter((f) => f.optionsEndpoint),
		[filters]
	);

	const queries = useMemo(
		() =>
			filtersWithEndpoints.map((filter) => ({
				queryKey: ["getOperationsManagementSummaryByClubFilterOptions", JSON.stringify({ filter: filter.field })] as const,
				queryFn: () => client.getOperationsManagementSummaryByClubFilterOptions.query({ filter: filter.field }),
				retry: false,
			})),
		[filtersWithEndpoints]
	);

	const results = useQueries({ queries });

	const dynamicOptions = useMemo(() => {
		const options: DynamicFilterOptions = {};
		results.forEach((result, i) => {
			const filter = filtersWithEndpoints[i];
			if (filter && result.data?.data) {
				options[filter.id] = result.data.data
					.filter((item) => item.value !== undefined && item.label !== undefined)
					.map((item) => ({
						value: item.value as string,
						label: item.label as string,
					}));
			}
		});
		return options;
	}, [results, filtersWithEndpoints]);

	const isLoading = results.some((r) => r.isLoading);
	const error = results.find((r) => r.error)?.error as Error | undefined;

	return {
		dynamicOptions,
		isLoading,
		error: error ?? null,
	};
}

export default useClubFilterOptions;

